import {
  getV1CompaniesCompanyIdRates,
  getV1CompaniesCompanyIdRatesTypeahead,
  GetV1CompaniesCompanyIdRatesTypeaheadData,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isString } from 'lodash-es';
import { useCallback } from 'react';

import { Rate } from '~hooks/useRates/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';

import { rateToRateBasicWithValue } from './utils';

interface UseRateDetailsProps {
  accountId?: string;
  companyId: string;
  driverId?: string;
  projectId?: string;
  serviceId?: string;
}

export default function useRateDetails({
  accountId,
  driverId,
  projectId,
  serviceId,
  companyId,
}: UseRateDetailsProps) {
  const fetchRates = useCallback(
    async (ownerType: RateOwnerType, args?: { query: string; cursor: string }) => {
      try {
        const isTypeaheadQuery = isString(args?.query) && Boolean(args?.query.length);

        const params: GetV1CompaniesCompanyIdRatesTypeaheadData['query'] = {
          'filter[account_id]': accountId || undefined,
          'filter[include_default]': true,
          'filter[owner_type]': ownerType,
          'filter[driver_id]': driverId || undefined,
          'filter[project_id]': projectId || undefined,
          'filter[service_id]': serviceId || undefined,
          'page[after]': args?.cursor || undefined,
          'page[limit]': 15,
          'search[query]': args?.query || undefined,
        };

        const response = isTypeaheadQuery
          ? await getV1CompaniesCompanyIdRatesTypeahead({
              query: params,
              path: { 'company-id': companyId },
            })
          : await getV1CompaniesCompanyIdRates({
              query: params,
              path: { 'company-id': companyId },
            });

        const rates = response.data.data.map((rate) => {
          return rateToRateBasicWithValue(Rate.parse(rate));
        });
        const pagination = extractPagination(response);

        return { data: rates, pagination };
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.rates.failed_to_fetch'));
      }
    },
    [accountId, companyId, driverId, projectId, serviceId],
  );

  const fetchCustomerRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(RateOwnerType.CUSTOMER, args);
    },
    [fetchRates],
  );

  const fetchVendorRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(RateOwnerType.VENDOR, args);
    },
    [fetchRates],
  );

  const fetchDriverRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(RateOwnerType.DRIVER, args);
    },
    [fetchRates],
  );

  const fetchPrimaryCustomerRate = useCallback(async () => {
    if (!accountId || !companyId) return null;

    try {
      const response = await getV1CompaniesCompanyIdRates({
        query: {
          'filter[account_id]': accountId,
          'filter[owner_type]': RateOwnerType.CUSTOMER,
        },
        path: { 'company-id': companyId },
      });

      const rates = response.data.data;
      const primaryRate = rates.find((rate) => rate.primary);

      if (primaryRate) {
        return rateToRateBasicWithValue(Rate.parse(primaryRate));
      }
      return null;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.rates.failed_to_fetch'));
      return null;
    }
  }, [accountId, companyId]);

  return {
    fetchCustomerRates,
    fetchVendorRates,
    fetchDriverRates,
    fetchPrimaryCustomerRate,
  };
}
